import { Trophy } from 'lucide-react'
import { computePressResults, computeSegmentMatchStatus, getPressLabel } from '../utils/scoringEngine'
import { getGameCourse } from '../constants/courses'
import { hexToRgba, getInitials } from '../utils/colors'
import { useRoundResults } from '../hooks/useGameScoring'

/**
 * Sticky banner showing the current round's team standings.
 * Displayed above the scorecard when a round is active.
 */
/**
 * Compute a short status string like "Team A 3 UP thru 5" or "ALL SQUARE thru 8"
 */
function computeRoundStatus(results, teamStructure) {
  if (!results?.holeResults) return null

  const holesPlayed = results.holeResults.filter((hr) => !hr.pending).length
  if (holesPlayed === 0) return null

  const thruLabel = `thru ${holesPlayed}`

  if (results.format === 'nassau' && results.type === 'stroke_play') {
    // Stroke play nassau (Duels): show aggregate score difference
    const totals = results.nassau.overall.totals
    if (!totals) return null
    const min = Math.min(...totals)
    const leaders = totals.filter((t) => t === min)
    if (leaders.length > 1) {
      return { text: `Tied ${thruLabel}`, color: 'var(--text-secondary)' }
    }
    const leadingIdx = totals.indexOf(min)
    const leadName = teamStructure[leadingIdx]?.name || `Team ${leadingIdx + 1}`
    const sorted = [...totals].sort((a, b) => a - b)
    const margin = sorted[1] - sorted[0]
    return { text: `${leadName} leads by ${margin} ${thruLabel}`, color: 'var(--accent-primary)' }
  }

  if (results.format === 'nassau' || results.format === 'per_nine') {
    // Match play: use overall holes/points won
    let wins
    if (results.format === 'nassau') {
      wins = results.nassau.overall.wins
    } else {
      // per_nine: sum wins across both nines
      wins = [0, 0]
      results.nineResults?.forEach((nr) => {
        wins[0] += nr.teamWins[0] || 0
        wins[1] += nr.teamWins[1] || 0
      })
    }

    const diff = wins[0] - wins[1]
    // High-Low: show point differential instead of "X UP" match play notation
    const isHighLow = results.holeResults?.some(hr => hr.teamPoints)
    if (isHighLow) {
      if (diff === 0) {
        return { text: `Tied ${thruLabel}`, color: 'var(--text-secondary)' }
      }
      const leadingTeam = diff > 0 ? 0 : 1
      const leadName = teamStructure[leadingTeam]?.name || `Team ${leadingTeam + 1}`
      return { text: `${leadName} +${Math.abs(diff)} pts ${thruLabel}`, color: 'var(--accent-primary)' }
    }
    if (diff === 0) {
      return { text: `All Square ${thruLabel}`, color: 'var(--text-secondary)' }
    }
    const leadingTeam = diff > 0 ? 0 : 1
    const leadName = teamStructure[leadingTeam]?.name || `Team ${leadingTeam + 1}`
    const margin = Math.abs(diff)
    return { text: `${leadName} ${margin} UP ${thruLabel}`, color: 'var(--accent-primary)' }
  }

  if (results.format === 'stableford') {
    const totals = results.stableford.overall.totals
    const max = Math.max(...totals)
    if (max === 0) return null
    const leaders = totals.filter((t) => t === max)
    if (leaders.length > 1) {
      return { text: `Tied at ${max} pts ${thruLabel}`, color: 'var(--text-secondary)' }
    }
    const leadingIdx = totals.indexOf(max)
    const leadName = teamStructure[leadingIdx]?.name || `Team ${leadingIdx + 1}`
    const sorted = [...totals].sort((a, b) => b - a)
    const margin = sorted[0] - sorted[1]
    return { text: `${leadName} leads by ${margin} pts ${thruLabel}`, color: 'var(--accent-primary)' }
  }

  if (results.format === 'six_pack') {
    const data = results.six_pack.overall.data || results.standings?.map((s) => ({ total: s.overallTotal, takenCount: 0 })) || []
    const validTeams = data.filter((d) => d.takenCount > 0)
    if (validTeams.length === 0) return null
    const min = Math.min(...validTeams.map((d) => d.total))
    const leaders = validTeams.filter((d) => d.total === min)
    if (leaders.length > 1) {
      return { text: `Tied ${thruLabel}`, color: 'var(--text-secondary)' }
    }
    const leadingIdx = data.indexOf(leaders[0])
    const leadName = teamStructure[leadingIdx]?.name || `Team ${leadingIdx + 1}`
    return { text: `${leadName} leads ${thruLabel}`, color: 'var(--accent-primary)' }
  }

  return null
}

/**
 * Build a status line for a press game (holes from press start to segment end).
 */
function buildPressStatus(results, press, teamStructure, label) {
  const segEnd = press.metadata?.segment === 'front' ? 9 : 18
  const pressHolesPlayed = results.holeResults.filter(
    (hr) => !hr.pending && hr.hole >= press.hole_start && hr.hole <= segEnd
  ).length

  if (pressHolesPlayed === 0) return null

  const pressResult = computePressResults(results, {
    segment: press.metadata?.segment,
    startHole: press.hole_start,
  }, teamStructure)

  if (!pressResult) return null

  const diff = pressResult.wins[0] - pressResult.wins[1]
  const thru = `thru ${pressHolesPlayed}`

  if (diff === 0) {
    return { text: `All Square ${thru} (${label})`, color: 'var(--text-secondary)' }
  }

  const leadingTeam = diff > 0 ? 0 : 1
  const leadName = teamStructure[leadingTeam]?.name || `Team ${leadingTeam + 1}`
  return { text: `${leadName} ${Math.abs(diff)} UP ${thru} (${label})`, color: 'var(--accent-primary)' }
}

/**
 * Build a status line for a Nassau segment (front/back/overall).
 */
function buildSegmentStatus(segmentWins, holesPlayed, teamStructure, label, isHighLow = false) {
  if (holesPlayed === 0) return null

  const diff = segmentWins[0] - segmentWins[1]
  const thru = `thru ${holesPlayed}`
  const suffix = label ? ` (${label})` : ''

  if (diff === 0) {
    const tiedText = isHighLow ? 'Tied' : 'All Square'
    return { text: `${tiedText} ${thru}${suffix}`, color: 'var(--text-secondary)' }
  }

  const leadingTeam = diff > 0 ? 0 : 1
  const leadName = teamStructure[leadingTeam]?.name || `Team ${leadingTeam + 1}`
  if (isHighLow) {
    return { text: `${leadName} +${Math.abs(diff)} pts ${thru}${suffix}`, color: 'var(--accent-primary)' }
  }
  return { text: `${leadName} ${Math.abs(diff)} UP ${thru}${suffix}`, color: 'var(--accent-primary)' }
}

/**
 * Compute status lines reflecting the current active game(s).
 * When presses are active, shows per-game statuses instead of a single overall line.
 *
 * During front 9 with F9 press:  1 row  — F9 Press status
 * During back 9 (after F9 press): 2 rows — Back 9 + Total
 *   + B9 Press row if back 9 is pressed
 *   + Total Press row if overall is pressed
 */
function computeRoundStatuses(results, teamStructure, presses) {
  if (!results?.holeResults) return []

  const holesPlayed = results.holeResults.filter((hr) => !hr.pending).length
  if (holesPlayed === 0) return []

  const activePresses = presses?.filter((p) => p.metadata?.pressing_team != null) || []

  if (activePresses.length === 0) {
    // No presses — fall back to single overall status
    const single = computeRoundStatus(results, teamStructure)
    return single ? [single] : []
  }

  // For nassau format with active presses, show per-game status lines
  if (results.format === 'nassau' && results.nassau) {
    const nassau = results.nassau
    const isHL = results.holeResults?.some(hr => hr.teamPoints)
    const segmentCounts = {}
    const getPressesForSeg = (seg) => {
      return activePresses.filter(p => p.metadata?.segment === seg).map(p => {
        segmentCounts[seg] = (segmentCounts[seg] || 0) + 1
        return { press: p, label: getPressLabel(seg, segmentCounts[seg]) }
      })
    }

    const lastScoredHole = Math.max(
      ...results.holeResults.filter((hr) => !hr.pending).map((hr) => hr.hole), 0
    )
    const onFrontNine = lastScoredHole <= 9 && !nassau.front.complete

    const statuses = []

    if (onFrontNine) {
      const frontPresses = getPressesForSeg('front')
      if (frontPresses.length > 0) {
        for (const { press, label } of frontPresses) {
          const ps = isHL ? buildHighLowPressStatus(results, press, teamStructure, label) : buildPressStatus(results, press, teamStructure, label)
          if (ps) statuses.push(ps)
        }
      } else {
        const s = buildSegmentStatus(nassau.overall.wins, holesPlayed, teamStructure, null, isHL)
        if (s) statuses.push(s)
      }
    } else {
      const backHolesPlayed = results.holeResults.filter((hr) => !hr.pending && hr.hole > 9).length
      const bs = buildSegmentStatus(nassau.back.wins, backHolesPlayed, teamStructure, 'Back 9', isHL)
      if (bs) statuses.push(bs)

      for (const { press, label } of getPressesForSeg('back')) {
        const ps = isHL ? buildHighLowPressStatus(results, press, teamStructure, label) : buildPressStatus(results, press, teamStructure, label)
        if (ps) statuses.push(ps)
      }

      const ts = buildSegmentStatus(nassau.overall.wins, holesPlayed, teamStructure, 'Total', isHL)
      if (ts) statuses.push(ts)

      for (const { press, label } of getPressesForSeg('overall')) {
        const ps = isHL ? buildHighLowPressStatus(results, press, teamStructure, label) : buildPressStatus(results, press, teamStructure, label)
        if (ps) statuses.push(ps)
      }
    }

    return statuses
  }

  // Stroke play formats with presses (stableford, six_pack, duels)
  if (results.format === 'stableford' || results.format === 'six_pack' ||
      (results.format === 'nassau' && results.type === 'stroke_play')) {
    const statuses = []
    const single = computeRoundStatus(results, teamStructure)
    if (single) statuses.push(single)

    // Add press status lines
    const spSegCounts = {}
    for (const press of activePresses) {
      const seg = press.metadata?.segment || 'overall'
      spSegCounts[seg] = (spSegCounts[seg] || 0) + 1
      const label = getPressLabel(seg, spSegCounts[seg])
      const ps = buildStrokePlayPressStatus(results, press, teamStructure, label)
      if (ps) statuses.push(ps)
    }

    return statuses
  }

  // Default fallback
  const single = computeRoundStatus(results, teamStructure)
  return single ? [single] : []
}

/**
 * Build a press status line for stroke play formats.
 */
function buildStrokePlayPressStatus(results, press, teamStructure, label) {
  const segEnd = press.metadata?.segment === 'front' ? 9 : 18
  const pressHolesPlayed = results.holeResults.filter(
    (hr) => !hr.pending && hr.hole >= press.hole_start && hr.hole <= segEnd
  ).length
  if (pressHolesPlayed === 0) return null

  const pressResult = computePressResults(results, {
    segment: press.metadata?.segment,
    startHole: press.hole_start,
  }, teamStructure)

  if (!pressResult) return null
  const thru = `thru ${pressHolesPlayed}`
  const totals = pressResult.totals || pressResult.wins

  if (results.format === 'stableford') {
    // Higher is better
    const diff = totals[0] - totals[1]
    if (diff === 0) return { text: `Tied ${thru} (${label})`, color: 'var(--text-secondary)' }
    const leadingTeam = diff > 0 ? 0 : 1
    const leadName = teamStructure[leadingTeam]?.name || `Team ${leadingTeam + 1}`
    return { text: `${leadName} +${Math.abs(diff)} pts ${thru} (${label})`, color: 'var(--accent-primary)' }
  }

  // Six Pack/Duels: lower is better
  const diff = totals[0] - totals[1]
  if (diff === 0) return { text: `Tied ${thru} (${label})`, color: 'var(--text-secondary)' }
  const leadingTeam = diff > 0 ? 1 : 0
  const leadName = teamStructure[leadingTeam]?.name || `Team ${leadingTeam + 1}`
  return { text: `${leadName} leads by ${Math.abs(diff)} ${thru} (${label})`, color: 'var(--accent-primary)' }
}

/**
 * Build a press status line for High-Low (point differential).
 */
function buildHighLowPressStatus(results, press, teamStructure, label) {
  const segEnd = press.metadata?.segment === 'front' ? 9 : 18
  const pressHolesPlayed = results.holeResults.filter(
    (hr) => !hr.pending && hr.hole >= press.hole_start && hr.hole <= segEnd
  ).length
  if (pressHolesPlayed === 0) return null

  const pressResult = computePressResults(results, {
    segment: press.metadata?.segment,
    startHole: press.hole_start,
  }, teamStructure)

  if (!pressResult) return null
  const thru = `thru ${pressHolesPlayed}`
  const diff = pressResult.wins[0] - pressResult.wins[1]

  if (diff === 0) return { text: `Tied ${thru} (${label})`, color: 'var(--text-secondary)' }
  const leadingTeam = diff > 0 ? 0 : 1
  const leadName = teamStructure[leadingTeam]?.name || `Team ${leadingTeam + 1}`
  return { text: `${leadName} +${Math.abs(diff)} pts ${thru} (${label})`, color: 'var(--accent-primary)' }
}

export default function RoundBanner({ gameData, matchPlayers, presses }) {
  const { results, teamStructure } = useRoundResults(gameData, matchPlayers)

  if (!results || teamStructure.length < 2) return null

  const gamePlayers = gameData?.game_players || []
  const course = getGameCourse(gameData)
  const holePars = course?.par || []

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '0.375rem',
      padding: '0.5rem 0',
      marginBottom: '0.5rem',
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.125rem', marginBottom: '0.25rem' }}>
        <span style={{
          fontSize: '1rem',
          fontWeight: '700',
          color: 'var(--text-primary)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        }}>
          Round Leaderboard
        </span>
      </div>

      <div style={{ display: 'flex', gap: '0.375rem' }}>
        {/* Match play nassau - 2 teams only (exclude High-Low which uses stroke play layout) */}
        {results.format === 'nassau' && results.nassau && results.type !== 'stroke_play' && teamStructure.length <= 2 && gameData?.game_type !== 'high_low' && (
          <NassauBanner nassau={results.nassau} teams={teamStructure} standings={results.standings} gamePlayers={gamePlayers} gameType={gameData?.game_type} presses={presses} results={results} matchFormat={gameData?.game_config?.matchFormat} />
        )}
        {/* All stroke play + High-Low: Duels, Stableford, Six Pack, High-Low */}
        {((results.format === 'nassau' && (results.type === 'stroke_play' || teamStructure.length > 2 || gameData?.game_type === 'high_low')) ||
          results.format === 'stableford' || results.format === 'six_pack') && (
          <StrokePlayColumnBanner results={results} teams={teamStructure} standings={results.standings} gamePlayers={gamePlayers} presses={presses} holePars={holePars} matchFormat={gameData?.game_config?.matchFormat} gameType={gameData?.game_type} />
        )}
        {/* Per-nine (Hero) - unchanged */}
        {results.format === 'per_nine' && results.nineResults && (
          <PerNineBanner nineResults={results.nineResults} teams={teamStructure} standings={results.standings} gamePlayers={gamePlayers} />
        )}
      </div>
    </div>
  )
}

function TeamPlayers({ playerIds, gamePlayers, justify = 'flex-start' }) {
  const players = playerIds.map(pid => {
    const gp = gamePlayers?.find(g => g.player_id === pid)
    return gp?.player || null
  }).filter(Boolean)

  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: justify, flexWrap: 'wrap', gap: '0.25rem' }}>
      {players.map((player, idx) => (
        <span key={player.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
          {idx > 0 && (
            <span style={{ marginRight: '0.125rem' }} />
          )}
          <span style={{
            width: '1.25rem',
            height: '1.25rem',
            borderRadius: '50%',
            backgroundColor: player.avatar_color,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: '700',
            fontSize: '0.5625rem',
            color: '#fff',
            flexShrink: 0,
          }}>
            {getInitials(player)}
          </span>
          <span style={{ fontSize: '0.8125rem', fontWeight: '600', color: player.avatar_color }}>
            {player.display_name?.split(' ')[0]}
          </span>
        </span>
      ))}
    </div>
  )
}

/**
 * Compute aggregate stroke totals for a press range.
 */
function computeStrokePlayPressTotals(results, startHole, endHole, numTeams, isHighLow) {
  const { holeResults, format } = results
  if (!holeResults) return new Array(numTeams).fill(0)

  return Array.from({ length: numTeams }, (_, teamIdx) => {
    let total = 0
    for (const hr of holeResults) {
      if (hr.hole >= startHole && hr.hole <= endHole && !hr.pending) {
        if (isHighLow) {
          total += hr.teamPoints?.[teamIdx] || 0
          continue
        }
        const ts = hr.teamScores?.find(t => t.teamIndex === teamIdx)
        if (!ts) continue
        if (format === 'nassau') {
          if (ts.bestBall != null) total += ts.bestBall
        } else if (format === 'stableford') {
          if (ts.teamPoints != null) total += ts.teamPoints
        } else if (format === 'six_pack') {
          for (const p of ts.playerNets || []) {
            if (p.taken === true && p.net != null) total += p.net
          }
        }
      }
    }
    return total
  })
}

/**
 * Determine the winner of a stroke play segment from totals.
 * Stableford/High-Low: higher is better. Duels/Six Pack: lower is better.
 */
function determineStrokePlayWinner(totals, format, isHighLow) {
  if (!totals || totals.length === 0 || totals.every(t => t === 0)) return null
  if (format === 'stableford' || isHighLow) {
    const max = Math.max(...totals)
    if (max === 0) return null
    return totals.filter(t => t === max).length === 1 ? totals.indexOf(max) : null
  }
  const min = Math.min(...totals)
  return totals.filter(t => t === min).length === 1 ? totals.indexOf(min) : null
}

/**
 * Compute per-team Six Pack details for a hole range: relative-to-par and drops remaining.
 */
function computeSixPackSegmentDetails(results, holePars, numTeams, startHole, endHole) {
  const { holeResults } = results
  if (!holeResults || !holePars.length) return null

  return Array.from({ length: numTeams }, (_, teamIdx) => {
    let relativeToPar = 0
    let allRelativeToPar = 0
    let takenCount = 0
    let scoredCount = 0
    let dropsUsed = 0
    let playersOnTeam = 0

    for (const hr of holeResults) {
      if (hr.hole < startHole || hr.hole > endHole || hr.pending) continue
      const par = holePars[hr.hole - 1] || 0
      const ts = hr.teamScores?.find(t => t.teamIndex === teamIdx)
      if (!ts) continue
      if (!playersOnTeam) playersOnTeam = ts.playerNets?.length || 2

      for (const p of ts.playerNets || []) {
        if (p.net != null) {
          allRelativeToPar += (p.net - par)
          scoredCount++
        }
        if (p.taken === true && p.net != null) {
          relativeToPar += (p.net - par)
          takenCount++
        } else if (p.taken === false) {
          dropsUsed++
        }
      }
    }

    const holesInSeg = endHole - startHole + 1
    const maxDrops = (playersOnTeam || 2) * holesInSeg - 12
    return { relativeToPar, allRelativeToPar, takenCount, scoredCount, dropsLeft: Math.max(0, maxDrops - dropsUsed) }
  })
}

/**
 * Normalize Duels/Stableford/Six Pack results into a common segment array.
 */
function normalizeStrokePlaySegments(results, presses, numTeams, holePars, matchFormat, gameType) {
  const segments = []
  const format = results.format
  const isHighLow = gameType === 'high_low'
  const activePresses = presses?.filter(p => p.metadata?.pressing_team != null) || []

  const getSegment = (segKey, label) => {
    let totals, winner, hasData, sixPackDetails, complete
    if (format === 'nassau') {
      const seg = results.nassau?.[segKey]
      totals = seg?.totals || seg?.wins || new Array(numTeams).fill(0)
      winner = seg?.winner ?? null
      hasData = totals.some(t => t > 0)
      complete = seg?.complete && winner != null
    } else if (format === 'stableford') {
      const seg = results.stableford?.[segKey]
      totals = seg?.totals || new Array(numTeams).fill(0)
      winner = seg?.winner ?? null
      hasData = totals.some(t => t > 0)
      complete = seg?.complete && winner != null
    } else if (format === 'six_pack') {
      const seg = results.six_pack?.[segKey]
      totals = seg?.data?.map(d => d.total) || new Array(numTeams).fill(0)
      hasData = (seg?.data || []).some(d => d.takenCount > 0)
      complete = seg?.complete || false
      const ranges = { front: [1, 9], back: [10, 18], overall: [1, 18] }
      const [start, end] = ranges[segKey] || [1, 18]
      sixPackDetails = computeSixPackSegmentDetails(results, holePars, numTeams, start, end)
      // Highlight based on displayed relative-to-par (lowest wins)
      winner = null
      if (sixPackDetails) {
        const live = sixPackDetails
          .map((spd, i) => ({ i, score: spd.takenCount > 0 ? spd.relativeToPar : spd.allRelativeToPar, has: spd.scoredCount > 0 }))
          .filter(t => t.has)
        if (live.length > 1) {
          const min = Math.min(...live.map(t => t.score))
          const leaders = live.filter(t => t.score === min)
          if (leaders.length === 1) winner = leaders[0].i
        }
      }
      // For Six Pack, "decided" requires all drops finalized
      complete = complete && sixPackDetails?.every(spd => spd.dropsLeft === 0)
    } else {
      totals = new Array(numTeams).fill(0)
      winner = null
      hasData = false
      complete = false
    }
    return { key: segKey, label, totals, winner, hasData, isPress: false, sixPackDetails, complete }
  }

  const pressSegCounts = {}
  const addPresses = (segKey, endHole) => {
    const segPresses = activePresses.filter(p => p.metadata?.segment === segKey)
    segPresses.forEach((press, idx) => {
      pressSegCounts[segKey] = (pressSegCounts[segKey] || 0) + 1
      const label = getPressLabel(segKey, pressSegCounts[segKey])
      const pressTotals = computeStrokePlayPressTotals(results, press.hole_start, endHole, numTeams, isHighLow)
      segments.push({
        key: `${segKey}_press_${idx}`, label,
        totals: pressTotals,
        winner: determineStrokePlayWinner(pressTotals, format, isHighLow),
        hasData: pressTotals.some(t => t > 0),
        isPress: true,
      })
    })
  }

  const isStandard = matchFormat === 'standard'
  if (!isStandard) {
    segments.push(getSegment('front', 'Front'))
    addPresses('front', 9)
    segments.push(getSegment('back', 'Back'))
    addPresses('back', 18)
  }
  segments.push(getSegment('overall', 'Total'))
  addPresses('overall', 18)

  return segments
}

function StrokePlayColumnBanner({ results, teams, standings, gamePlayers, presses, holePars, matchFormat, gameType }) {
  const numTeams = teams.length
  const segments = normalizeStrokePlaySegments(results, presses, numTeams, holePars, matchFormat, gameType)
  const isSixPack = results.format === 'six_pack'
  const isHighLow = gameType === 'high_low'

  const formatRelToPar = (rel) => {
    if (rel === 0) return 'E'
    return rel > 0 ? `+${rel}` : `${rel}`
  }

  return (
    <div style={{
      width: '100%',
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '0.5rem',
      border: '1px solid var(--border-color)',
      padding: '0.625rem 0.75rem',
    }}>
      {/* Team headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${numTeams}, 1fr)`,
        gap: '0.375rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid var(--border-color)',
      }}>
        {teams.map((team, i) => (
          <div key={i} style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: '0.8125rem',
              fontWeight: '700',
              color: 'var(--text-primary)',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              marginBottom: '0.25rem',
            }}>
              {team.name}
            </div>
            <TeamPlayers playerIds={team.playerIds} gamePlayers={gamePlayers} justify="center" />
          </div>
        ))}
      </div>

      {/* Segment rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', marginTop: '0.5rem' }}>
        {segments.map((seg) => (
          <div key={seg.key} style={{
            ...(seg.isPress ? {
              padding: '0.375rem',
              borderRadius: '0.5rem',
              border: '1px dashed rgba(245, 158, 11, 0.35)',
            } : {}),
          }}>
            <div style={{
              fontSize: '0.6875rem',
              fontWeight: '600',
              color: seg.isPress ? 'var(--accent-warning)' : 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
              textAlign: 'center',
              marginBottom: '0.25rem',
            }}>
              {seg.label}
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${numTeams}, 1fr)`,
              gap: '0.375rem',
            }}>
              {seg.totals.map((total, i) => {
                const isWinner = seg.winner === i
                const spd = seg.sixPackDetails?.[i]
                const hasTeamData = isSixPack ? (spd?.scoredCount > 0) : seg.hasData
                const showDrops = isSixPack && spd?.scoredCount > 0 && (seg.key === 'front' || seg.key === 'back')

                let displayValue
                if (!hasTeamData) {
                  displayValue = '\u2013'
                } else if (isSixPack && spd) {
                  displayValue = formatRelToPar(spd.takenCount > 0 ? spd.relativeToPar : spd.allRelativeToPar)
                } else {
                  displayValue = total
                }

                return (
                  <div key={i} style={{
                    textAlign: 'center',
                    padding: '0.5rem 0.25rem',
                    borderRadius: '0.375rem',
                    backgroundColor: 'var(--bg-elevated)',
                    border: `1px solid ${isWinner ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                    boxShadow: 'var(--card-shadow, none)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem' }}>
                      {isWinner && seg.complete && <Trophy size={14} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />}
                      <span style={{
                        fontSize: '1.25rem',
                        fontWeight: isWinner ? '800' : '600',
                        color: isWinner ? 'var(--accent-primary)' : hasTeamData ? 'var(--text-primary)' : 'var(--text-muted)',
                      }}>
                        {displayValue}
                      </span>
                    </div>
                    {showDrops && (
                      <div style={{
                        fontSize: '0.625rem',
                        color: 'var(--text-muted)',
                        marginTop: '0.125rem',
                      }}>
                        {spd.dropsLeft} drop{spd.dropsLeft !== 1 ? 's' : ''} left
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// computeSegmentMatchStatus is now imported from scoringEngine

function NassauBanner({ nassau, teams, standings, gamePlayers, gameType, presses, results, matchFormat }) {
  const numHoles = results?.holeResults?.length || 18
  const is18 = numHoles >= 18

  // Active presses — no longer override segment winners (presses run alongside)
  const effectivePresses = presses?.filter((p) => p.metadata?.pressing_team != null) || []
  const effectiveNassau = nassau

  const holeResults = results?.holeResults || []

  // Build rows: { label, segmentKey, startHole, endHole, isPress, press? }
  const rows = []
  const nassauSegCounts = {}
  const addPressRows = (segKey, endHole) => {
    const segPresses = effectivePresses.filter(p => p.metadata?.segment === segKey)
    segPresses.forEach((press) => {
      nassauSegCounts[segKey] = (nassauSegCounts[segKey] || 0) + 1
      const label = getPressLabel(segKey, nassauSegCounts[segKey])
      rows.push({ label, segmentKey: segKey, startHole: press.hole_start, endHole, isPress: true, press })
    })
  }

  const isStandard = matchFormat === 'standard'

  if (is18 && !isStandard) {
    rows.push({ label: 'Front', segmentKey: 'front', startHole: 1, endHole: 9 })
    addPressRows('front', 9)

    rows.push({ label: 'Back', segmentKey: 'back', startHole: 10, endHole: 18 })
    addPressRows('back', 18)

    rows.push({ label: 'Total', segmentKey: 'overall', startHole: 1, endHole: 18 })
    addPressRows('overall', 18)
  } else if (is18 && isStandard) {
    rows.push({ label: 'Total', segmentKey: 'overall', startHole: 1, endHole: 18 })
    addPressRows('overall', 18)
  } else {
    rows.push({ label: 'Total', segmentKey: 'overall', startHole: 1, endHole: numHoles })
    addPressRows('overall', numHoles)
  }

  // Team info
  const teamA = teams[0] || { name: 'Team 1', playerIds: [] }
  const teamB = teams[1] || { name: 'Team 2', playerIds: [] }

  return (
    <div style={{
      width: '100%',
      backgroundColor: 'var(--bg-secondary)',
      borderRadius: '0.5rem',
      border: '1px solid var(--border-color)',
      padding: '0.625rem 0.75rem',
    }}>
      {/* Team headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: '0.5rem',
        marginBottom: '0.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid var(--border-color)',
      }}>
        <div>
          <div style={{
            fontSize: '0.8125rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.25rem',
          }}>
            {teamA.name}
          </div>
          <TeamPlayers playerIds={teamA.playerIds} gamePlayers={gamePlayers} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.6875rem', color: 'var(--text-primary)', fontWeight: '600' }}>
          vs
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{
            fontSize: '0.8125rem',
            fontWeight: '700',
            color: 'var(--text-primary)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom: '0.25rem',
          }}>
            {teamB.name}
          </div>
          <TeamPlayers playerIds={teamB.playerIds} gamePlayers={gamePlayers} justify="flex-end" />
        </div>
      </div>

      {/* Scoreboard rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.map((row, idx) => {
          let status
          let decided = false

          if (row.isPress) {
            // For press rows, compute from press start hole
            status = computeSegmentMatchStatus(holeResults, row.startHole, row.endHole)
            if (status?.winner != null) {
              const pressHolesTotal = row.endHole - row.startHole + 1
              const pressHolesPlayed = holeResults.filter(hr => !hr.pending && hr.hole >= row.startHole && hr.hole <= row.endHole).length
              decided = pressHolesPlayed >= pressHolesTotal || status.text.includes('&')
            }
          } else {
            // Original segment — plays out based on actual results (no concession)
            status = computeSegmentMatchStatus(holeResults, row.startHole, row.endHole)
            decided = (effectiveNassau[row.segmentKey]?.complete && status?.winner != null) || (status?.text?.includes('&'))
          }

          const isAllSquare = status && status.winner === null
          const isPress = row.isPress

          const cardBase = {
            borderRadius: '0.5rem',
            padding: '0.625rem 0.5rem',
            minHeight: '2.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.375rem',
          }

          return (
            <div
              key={idx}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                gap: '0.5rem',
                ...(isPress ? {
                  padding: '0.5rem',
                  borderRadius: '0.625rem',
                  border: '1px dashed rgba(245, 158, 11, 0.35)',
                } : {
                  padding: '0.5rem',
                  borderRadius: '0.625rem',
                  border: '1px solid transparent',
                }),
              }}
            >
              {/* Team A card */}
              <div style={{
                ...cardBase,
                backgroundColor: 'var(--bg-elevated)',
                border: `1px solid ${status?.winner === 0 ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                boxShadow: 'var(--card-shadow, none)',
              }}>
                {status?.winner === 0 && (
                  <>
                    {decided && <Trophy size={14} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />}
                    <span style={{
                      fontSize: '1.125rem',
                      fontWeight: '800',
                      color: 'var(--accent-primary)',
                    }}>
                      {status.text}
                    </span>
                  </>
                )}
                {isAllSquare && (
                  <span style={{
                    fontSize: '0.9375rem',
                    fontWeight: '700',
                    color: 'var(--text-secondary)',
                  }}>
                    TIED
                  </span>
                )}
              </div>

              {/* Center label */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                minWidth: '5rem',
              }}>
                <span style={{
                  fontSize: '0.8125rem',
                  fontWeight: '700',
                  color: isPress ? 'var(--accent-warning)' : 'var(--text-primary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}>
                  {row.label}
                </span>
              </div>

              {/* Team B card */}
              <div style={{
                ...cardBase,
                backgroundColor: 'var(--bg-elevated)',
                border: `1px solid ${status?.winner === 1 ? 'var(--accent-primary)' : 'var(--border-color)'}`,
                boxShadow: 'var(--card-shadow, none)',
              }}>
                {status?.winner === 1 && (
                  <>
                    <span style={{
                      fontSize: '1.125rem',
                      fontWeight: '800',
                      color: 'var(--accent-primary)',
                    }}>
                      {status.text}
                    </span>
                    {decided && <Trophy size={14} style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />}
                  </>
                )}
                {isAllSquare && (
                  <span style={{
                    fontSize: '0.9375rem',
                    fontWeight: '700',
                    color: 'var(--text-secondary)',
                  }}>
                    TIED
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}


function PerNineBanner({ nineResults, teams, standings, gamePlayers }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', width: '100%' }}>
      {standings.map((s, i) => {
        const teamPlayerIds = teams[s.teamIndex]?.playerIds || []
        return (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '0.5rem',
            border: '1px solid var(--border-color)',
            gap: '0.75rem',
          }}
        >
          <div style={{
            fontSize: '0.75rem',
            fontWeight: '700',
            color: 'var(--text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            whiteSpace: 'nowrap',
            minWidth: '3.5rem',
          }}>
            {s.teamName}
          </div>
          <TeamPlayers playerIds={teamPlayerIds} gamePlayers={gamePlayers} />
          <div style={{
            fontSize: '1.5rem',
            fontWeight: '800',
            color: s.points > 0 ? 'var(--accent-primary)' : 'var(--text-primary)',
            lineHeight: 1,
            minWidth: '2rem',
            textAlign: 'right',
          }}>
            {s.points}
          </div>
          <div style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', minWidth: '1.25rem' }}>pts</div>
        </div>
        )
      })}

      {/* Nine detail */}
      <div style={{
        padding: '0.375rem 0.75rem',
        backgroundColor: 'var(--bg-secondary)',
        borderRadius: '0.5rem',
        border: '1px solid var(--border-color)',
        display: 'flex',
        justifyContent: 'space-around',
        gap: '0.5rem',
      }}>
        {nineResults.map((nr) => (
          <div key={nr.label} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.375rem',
          }}>
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', fontWeight: '600' }}>
              {nr.label === 'Front 9' ? 'F9' : 'B9'}
            </span>
            <span style={{
              fontSize: '0.8125rem',
              fontWeight: '700',
              color: nr.winner != null ? 'var(--accent-primary)' : 'var(--text-muted)',
            }}>
              {nr.teamWins[0]}-{nr.teamWins[1]}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

